import { trackEvent } from '@repo/common/analytics';
import IconEmojiSelector from '@repo/common/components/IconEmojiSelector';
import { iconEmojiSelectorValueToString } from '@repo/common/components/IconEmojiSelector/utils';
import { EntityType } from '@repo/common/enums/entityType';
import { trimMarkdown } from '@repo/common/utils/sanitizeMarkdownString.ts';
import { useMount } from 'ahooks';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router';
import {
	useAddToCollection,
	useAuthUser,
	useCollectionTabs,
	useUpdateCollection,
} from '../../api';
import { useSuspenseSecodaEntity } from '../../api/hooks/secodaEntity/useSuspenseSecodaEntity';
import TabsPanel from '../../components/CollectionsEntityTabs/CollectionsTabsPanel.tsx';
import { getEntityPageSidebarProps } from '../../components/EntityDrawer/utils';
import EntityPageLayout from '../../components/EntityPageLayout';
import EntityPageLayoutSkeleton from '../../components/EntityPageLayout/EntityPageLayoutSkeleton.tsx';
import { getDefaultTab } from '../../components/EntityPageLayout/utils.ts';
import OverviewPageTabs from '../../components/Overview/OverviewPageTabs';
import ResourceSelectorPopover from '../../components/ResourceSelectorPopover/ResourceSelectorPopover.tsx';
import type { SecodaEntity } from '../../lib/models/index.ts';
import { useCheckEntityUpdatePermission } from '../../utils/authorization/roles.ts';
import { useTabNavigate } from '../../utils/hook/useTabNavigate.ts';
import { useParamsIdSuffixUuid } from '../../utils/hook/utils';
import { mapEntityTypeToIcon } from '../../utils/integrationLogo';
import type { DjangoValueType } from '../TemplatePage/types';

const InnerCollectionPage = observer(({ id }: { id: string }) => {
	const { tab: urlTabParam } = useParams();
	const { user, workspace } = useAuthUser();
	const navigateToTab = useTabNavigate();

	const { data: collection } = useSuspenseSecodaEntity({
		id,
	});

	useMount(() => {
		trackEvent('collection_entity/open', {}, user, workspace);
	});

	const { data: collectionTabs, isLoading: isTabsLoading } =
		useCollectionTabs(id);

	const canUpdateEntity = useCheckEntityUpdatePermission({
		entity: collection,
	});
	const readOnly = !canUpdateEntity;

	const { mutateAsync } = useUpdateCollection({
		disableInvalidation: true,
		options: {
			onSuccess: () => {
				trackEvent('collection/properties/update', {}, user, workspace!);
			},
		},
	});

	const { addToCollection } = useAddToCollection();

	const handleAddToCollection = useCallback(
		async (entity: SecodaEntity) => {
			await addToCollection(id, entity);
			navigateToTab(entity.entity_type);
		},
		[addToCollection, id, navigateToTab]
	);

	const updateCollectionEntity = useCallback(
		async (key: string, value: DjangoValueType) => {
			await mutateAsync({
				data: {
					id,
					[key]: value,
				},
			});
		},
		[id, mutateAsync]
	);

	const handleIconChange = useCallback(
		(value: string) => {
			updateCollectionEntity('icon', value);
		},
		[updateCollectionEntity]
	);

	const customActions = useMemo(
		() => (
			<ResourceSelectorPopover
				key="add-to-collection"
				targetButtonIconName="plus"
				targetButtonLabel="Add resource"
				collectionId={id}
				types={[
					'table',
					'dashboard',
					'job',
					'metric',
					'dictionary_term',
					'question',
					'document',
					'collection',
					'bi_dashboard',
					'bi_metric',
					'glossary',
				]}
				updateEntity={handleAddToCollection}
			/>
		),
		[id, handleAddToCollection]
	);

	const hasDocumentation =
		trimMarkdown(collection?.definition ?? '').length > 2;

	const tabs = useMemo(
		() =>
			collectionTabs?.map((tab) => ({
				value: tab.value,
				label: tab.label,
				component: <TabsPanel activeTab={tab.value} collectionId={id} />,
			})),
		[collectionTabs, id]
	);

	const icon = useMemo(
		() => (
			<IconEmojiSelector
				value={
					collection?.icon ??
					iconEmojiSelectorValueToString(
						mapEntityTypeToIcon(EntityType.collection)!
					)
				}
				onChange={handleIconChange}
				entityType={collection?.entity_type}
				readOnly={readOnly}
			/>
		),
		[collection?.icon, collection?.entity_type, readOnly, handleIconChange]
	);

	const defaultTab = useMemo(() => getDefaultTab(tabs || []), [tabs]);

	const currentTab =
		urlTabParam ??
		defaultTab ??
		(hasDocumentation
			? 'documentation'
			: (collectionTabs?.[0]?.value ?? 'documentation'));

	if (isTabsLoading || !collection) {
		return <EntityPageLayoutSkeleton withTabs withTable />;
	}

	return (
		<EntityPageLayout
			key={collection.id}
			name="Collection"
			entity={collection as never}
			icon={icon}
			updateEntity={updateCollectionEntity}
			withDescription
			withCustomActions={customActions}
			customActionsPosition={'right'}
			{...getEntityPageSidebarProps(EntityType.collection)}
		>
			<OverviewPageTabs
				defaultTab={defaultTab}
				currentTab={currentTab}
				tabs={tabs ?? []}
				onTabChange={navigateToTab}
			/>
		</EntityPageLayout>
	);
});

// By memoizing the inner component, we can avoid re-rendering the entire page on tab changes and only re-render the tab panel
export default function CollectionPage() {
	const id = useParamsIdSuffixUuid();

	return <InnerCollectionPage id={id} />;
}
